import type { DayLog, KayaState, StepSample } from "./types";

/**
 * Walks the counter noticed: a stretch of the day's step samples brisk and long enough to have been
 * a walk that nobody timed (ADR-063). The counter app samples every fifteen minutes, so the ends
 * of a noticed walk are blurred by up to a quarter of an hour either side — which is why one is
 * only ever offered ("was that a walk?") and saved on a tap, and why it carries no cadence word.
 *
 * Pure: the samples are cumulative day-to-date counts, as the phone posts them.
 */

export interface NoticedWalk {
  /** ISO strings (toISOString) */
  startedAt: string;
  endedAt: string;
  secs: number;
  steps: number;
}

/** Steps a minute across a sample interval: a busy kitchen hour is about 40. */
export const NOTICED_MIN_RATE = 60;
/** Intervals shorter than a minute are live-mode noise (the ten-second readings of a timed walk). */
export const NOTICED_MIN_GAP_MS = 60_000;
/** Longer than this the phone was asleep (Doze), and an average over it says nothing; not read. */
export const NOTICED_MAX_GAP_MS = 30 * 60_000;
export const NOTICED_MIN_SECS = 20 * 60;
export const NOTICED_MIN_STEPS = 1_500;
/** A timed walk or run claims this much either side: its boundary readings blur into the next sample. */
export const NOTICED_PAD_MS = 5 * 60_000;

export interface TimedWindow {
  startedAt: string;
  secs: number;
}

/**
 * Maximal runs of consecutive sample intervals whose rate is at least `NOTICED_MIN_RATE`, kept when
 * at least twenty minutes and 1,500 steps, minus any run that overlaps a timed window padded by
 * five minutes. Readings closer than a minute to the last one used are skipped, so a live
 * session's ten-second samples neither make nor break a run; a gap over thirty minutes ends one.
 */
export function noticedWalks(samples: readonly StepSample[], timed: readonly TimedWindow[]): NoticedWalk[] {
  const sorted = [...samples].filter((s) => Number.isFinite(s.t) && Number.isFinite(s.steps)).sort((a, b) => a.t - b.t);
  const kept: StepSample[] = [];
  for (const s of sorted) {
    const last = kept[kept.length - 1];
    if (!last || s.t - last.t >= NOTICED_MIN_GAP_MS) kept.push(s);
  }

  const runs: { from: StepSample; to: StepSample }[] = [];
  let open: { from: StepSample; to: StepSample } | null = null;
  for (let i = 1; i < kept.length; i++) {
    const a = kept[i - 1];
    const b = kept[i];
    const gap = b.t - a.t;
    const brisk = gap <= NOTICED_MAX_GAP_MS && b.steps - a.steps >= (NOTICED_MIN_RATE * gap) / 60_000;
    if (brisk) {
      if (open && open.to === a) open.to = b;
      else {
        open = { from: a, to: b };
        runs.push(open);
      }
    } else open = null;
  }

  const windows = timed
    .map((w) => {
      const start = Date.parse(w.startedAt);
      return { from: start - NOTICED_PAD_MS, to: start + w.secs * 1000 + NOTICED_PAD_MS };
    })
    .filter((w) => Number.isFinite(w.from) && Number.isFinite(w.to));

  const out: NoticedWalk[] = [];
  for (const r of runs) {
    const secs = Math.round((r.to.t - r.from.t) / 1000);
    const steps = r.to.steps - r.from.steps;
    if (secs < NOTICED_MIN_SECS || steps < NOTICED_MIN_STEPS) continue;
    if (windows.some((w) => r.from.t < w.to && r.to.t > w.from)) continue;
    out.push({ startedAt: new Date(r.from.t).toISOString(), endedAt: new Date(r.to.t).toISOString(), secs, steps });
  }
  return out;
}

/**
 * What the day already timed: its walks (a noticed walk once saved among them, so it is not
 * offered twice) and the treadmill run. A walk's window is the wall clock from its start to its
 * end where both are known, since a paused walk spans more than its active seconds.
 */
export function timedWindows(L: DayLog | undefined): TimedWindow[] {
  if (!L) return [];
  const out: TimedWindow[] = [];
  for (const w of L.walks || []) {
    const span = (Date.parse(w.at) - Date.parse(w.startedAt)) / 1000;
    out.push({ startedAt: w.startedAt, secs: Number.isFinite(span) ? Math.max(w.secs, span) : w.secs });
  }
  const t = L.treadmill;
  if (t?.startedAt) {
    const span = (Date.parse(t.at) - Date.parse(t.startedAt)) / 1000;
    const secs = typeof t.mins === "number" ? t.mins * 60 : Number.isFinite(span) ? span : 0;
    out.push({ startedAt: t.startedAt, secs });
  }
  return out;
}

/** "9:10–9:40 · about 3,100 steps", the offer's figure: the steps to the nearest hundred, because
 *  the ends are fifteen-minute samples and the count is no better than that. */
export function noticedLine(w: NoticedWalk, clock: (iso: string) => string): string {
  const about = Math.round(w.steps / 100) * 100;
  return `${clock(w.startedAt)}–${clock(w.endedAt)} · about ${about.toLocaleString("en-GB")} steps`;
}

export const NOTICED_OFF_CAP = 50;

/** The phone's "No" list with one more walk said no to: newest last, the oldest dropped past 50. */
export function withNoticedOff(list: readonly string[] | undefined, startedAt: string): string[] {
  const out = (list || []).filter((s) => s !== startedAt);
  out.push(startedAt);
  return out.slice(-NOTICED_OFF_CAP);
}

/** The one walk to offer: the earliest the day noticed that was not said no to; null when none. */
export function noticedOffer(found: readonly NoticedWalk[], off: readonly string[] | undefined): NoticedWalk | null {
  const no = new Set(off || []);
  return found.find((w) => !no.has(w.startedAt)) ?? null;
}

/**
 * The walk to offer on `ds`, or null: the day's cached samples (today's only), less what was
 * timed — the day's walks and run, and an open session from its start to now, which is not logged
 * yet — and less what this phone was told was not a walk.
 */
export function noticedOfferFor(
  S: KayaState,
  ds: string,
  off: readonly string[] | undefined,
  open: TimedWindow | null = null
): NoticedWalk | null {
  const samples = S.sampleCache?.date === ds ? S.sampleCache.samples : [];
  if (!samples.length) return null;
  const timed = timedWindows(S.logs[ds]);
  if (open) timed.push(open);
  return noticedOffer(noticedWalks(samples, timed), off);
}
